import React, { ReactNode } from 'react';
import styled from 'astroturf/react';
import Dropzone, { DropFilesEventHandler } from 'react-dropzone';

import { DEFAULT_ACCEPTED_FILE_EXTENSIONS } from 'js/utils/file_uploader';

import { Button } from 'js/design_system/Button';

/**
 * A styled file upload component that both accepts drag and drop and
 * a button to open a platform-specific file picker.
 *
 * @param {DropFilesEventHandler} onNewFiles callback for when new files are dropped or selected by the user. Receives both accepted and rejected files.
 * @param {string[]=} [acceptedFileExtensions=DEFAULT_ACCEPTED_FILE_EXTENSIONS] array of file extensions to accept, e.g. ['.pdf', '.jpg']
 * @param {boolean} [disabled=false] Renders in a disabled state, disables the button and drag and drop.
 * @param {boolean} [disableDropzoneClick=false] Disables click-to-open on the dropzone background. Drag-and-drop and the upload button still work.
 * @param {boolean} [multiple=true] Allows multiple files to be dropped or selected, and changes the hint.
 * @param {boolean} [primary=false] Whether to render the button as a primary CTA.
 * @param {boolean} [secondary=false] Whether to render the button as a secondary CTA.
 * @param {boolean} [small=false] Renders a small version of the component for use in lists and other space-limited contexts.
 * @param {object} [style] Style overrides for the dropzone body.
 * @param {object} [dropzoneStyle] Style overrides for the outer dropzone container.
 * @param {ReactNode} [children] Optional children to be rendered within the component, above the dropzone.
 * @param {ReactNode} [footer] Optional content rendered inside the dropzone, below the note.
 * @param {() => void} [onClick] Optional callback for when file upload is clicked.
 * @param {string} [buttonText] Custom text for the upload button. Defaults to "Choose files" or "Choose a file" based on `multiple`.
 * @param {string} [buttonIcon] Icon name to display on the upload button.
 *
 * ## Basic Usage
 * @example
 *
 * ```tsx
 * import FileUpload from 'js/design_system/FileUpload';
 *
 * <FileUpload
 *   onNewFiles={(acceptedFiles, rejectedFiles) => { console.log(acceptedFiles, rejectedFiles) }}
 * />
 * ```
 */

export type FileUploadProps = {
  onNewFiles: DropFilesEventHandler;
  description?: string;
  acceptedFileExtensions?: string[];
  note?: string;
  disabled?: boolean;
  disableDropzoneClick?: boolean;
  multiple?: boolean;
  primary?: boolean;
  secondary?: boolean;
  small?: boolean;
  style?: object;
  dropzoneStyle?: object;
  children?: ReactNode;
  footer?: ReactNode;
  onClick?: () => void;
  buttonText?: string;
  buttonIcon?: string;
};

const UploadZone = ({
  onNewFiles,
  description,
  acceptedFileExtensions = DEFAULT_ACCEPTED_FILE_EXTENSIONS,
  note,
  disabled = false,
  disableDropzoneClick = false,
  multiple = true,
  primary = false,
  secondary = false,
  small = false,
  style = {},
  dropzoneStyle = {},
  children,
  footer,
  onClick,
  buttonText,
  buttonIcon,
}: FileUploadProps) => {
  const dropzoneRef = React.useRef<Dropzone>(null);

  const acceptedFiles = acceptedFileExtensions
    .map((extension) =>
      // Don't modify mime types or already-prefixed extensions
      extension[0] === '.' || extension.includes('/')
        ? extension
        : `.${extension}`,
    )
    .join(', ');

  const handleButtonClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dropzoneRef.current?.open();
  }, []);

  return (
    // @ts-ignore - react-dropzone types haven't been updated to handle explicit children from react 18
    <Dropzone
      ref={dropzoneRef}
      accept={acceptedFiles}
      disableClick={disabled || disableDropzoneClick}
      onDrop={disabled ? undefined : onNewFiles}
      multiple={multiple}
      style={{ maxWidth: '100%', flex: 1, ...dropzoneStyle }}
      onClick={disabled ? undefined : onClick}
    >
      {children}

      <DropzoneBody style={style}>
        <DropzoneLabel compact={small}>
          Drag and drop{' '}
          {description ? (
            <strong>{description}</strong>
          ) : multiple ? (
            'files'
          ) : (
            'a file'
          )}
        </DropzoneLabel>
        <DropzoneLabel compact={small}>or</DropzoneLabel>
        <ButtonContainer>
          <Button
            type="button"
            disabled={disabled}
            size={small ? 'sm' : 'lg'}
            secondary={secondary}
            inverse={!primary && !(secondary ?? false)}
            icon={buttonIcon}
            onClick={disableDropzoneClick ? handleButtonClick : undefined}
          >
            {buttonText || `Choose ${multiple ? 'files' : 'a file'}`}
          </Button>
        </ButtonContainer>
        <Note>{note}</Note>
        {footer}
      </DropzoneBody>
    </Dropzone>
  );
};

export default UploadZone;

const DropzoneLabel = styled.div<{ compact?: boolean }>`
  font-size: 22px;
  font-weight: 400;
  margin-bottom: 8px;
  line-height: 28px;
  text-align: center;

  &.compact {
    font-size: 16px;
    line-height: 20px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 8px;
`;

const DropzoneBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 540px;
  padding: 24px;
  background-color: #fff;
  border: 3px dashed #dddde2;
`;

export const Note = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__small;
  color: var(--text-secondary);

  margin-top: 16px;
`;
